import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { useEffect } from "react";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <SeoHead
        title="Privacy Policy"
        description="Read the Tripsyfy privacy policy — how we collect, use, and protect your personal information."
        canonical="https://tripsyfy.com/privacy-policy"
        noIndex
      />
      <Navbar />

      <section className="pt-24 lg:pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="heading-xl text-primary mb-4">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-10">Last updated: April 7, 2026</p>

          <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">

            <p>Tripsyfy ("Tripsyfy," "we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, and protect the information you submit through our website.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">1. Who We Are</h2>
            <p>Tripsyfy is a travel services business that helps customers with travel inquiries, trip planning, and travel-related assistance.</p>
            <p>If you have any questions about this Privacy Policy, please contact us at:</p>
            <ul className="list-none space-y-1 mt-3">
              <li>Email: <a href="mailto:hello@tripsyfy.com" className="text-accent hover:underline">hello@tripsyfy.com</a></li>
              <li>Phone: <a href="tel:+19726661122" className="text-accent hover:underline">+1 972-666-1122</a></li>
              <li>Address: Edison, New Jersey, USA</li>
            </ul>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">2. Information We Collect</h2>
            <p>When you submit an inquiry through our website, we may collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your name</li>
              <li>Your email address</li>
              <li>Your phone number</li>
              <li>Your travel details, such as departure city, destination, travel dates, number of travelers, and similar trip-related information you choose to provide</li>
            </ul>
            <p>We only collect information that you voluntarily submit to us through our website forms or direct communications.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">3. How We Use Your Information</h2>
            <p>We use the information you provide to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>respond to your inquiries;</li>
              <li>understand your travel needs;</li>
              <li>prepare quotes, suggestions, or itinerary options;</li>
              <li>communicate with you about your requested trip; and</li>
              <li>provide travel-related assistance and customer support.</li>
            </ul>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">4. How We Share Your Information</h2>
            <p>We do not sell or rent your personal information.</p>
            <p>We may share your information only in the following situations:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>with travel suppliers or partners, but only if needed to help plan or arrange travel at your request;</li>
              <li>with service providers who help us operate our business or website; or</li>
              <li>when required by law, regulation, legal process, or government request.</li>
            </ul>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">5. Data Retention</h2>
            <p>We keep your information only for as long as reasonably necessary to respond to your inquiry, provide requested services, maintain business records, or comply with legal obligations.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">6. Your Choices</h2>
            <p>You may contact us at any time to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>request access to the personal information you submitted to us;</li>
              <li>request that we correct inaccurate information; or</li>
              <li>request that we delete your information, subject to any legal or legitimate business retention needs.</li>
            </ul>
            <p>To make a request, contact us at <a href="mailto:hello@tripsyfy.com" className="text-accent hover:underline">hello@tripsyfy.com</a>.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">7. Data Security</h2>
            <p>We take reasonable steps to protect the information you submit through our website. However, no method of internet transmission or electronic storage is completely secure, and we cannot guarantee absolute security.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">8. Children's Privacy</h2>
            <p>Our website and services are not directed to children under the age of 13, and we do not knowingly collect personal information from children through our website.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">9. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. Any updates will be posted on this page with a revised "Last updated" date.</p>

            <h2 className="text-lg font-bold text-primary mt-8 mb-4">10. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or how we handle your information, please contact us at:</p>
            <ul className="list-none space-y-1 mt-3">
              <li className="font-medium text-primary">Tripsyfy</li>
              <li>Edison, New Jersey, USA</li>
              <li>Email: <a href="mailto:hello@tripsyfy.com" className="text-accent hover:underline">hello@tripsyfy.com</a></li>
              <li>Phone: <a href="tel:+19726661122" className="text-accent hover:underline">+1 972-666-1122</a></li>
            </ul>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
